import React, { useCallback, useEffect } from 'react';
import { Box, useTheme } from '@mui/material';
import {
    DossierDocumentsValidation,
    DossierValidationCustomHeaderActions,
    Layout,
    LAYOUT_DEFAULT,
    LAYOUT_DOCUMENTS_1_3,
    LAYOUT_FORMS_FULL,
    useDossierFormulaireContext,
    useDossierRoutingContext,
    useDossierValidationHeader,
    useSelectedDossierContext,
} from '@europrocurement/l2d-modules';
import { enqueueSnackbar } from 'notistack';
import { useNavigate } from 'react-router';
import { ConfirmationModal, useFullLayout, useModal } from '@europrocurement/flexy-components';

import DossierValidationBreadcrumb from '../DossierValidationBreadcrumb/DossierValidationBreadcrumb';
import DossierFormsValidation from '../DossierFormsValidation/DossierFormsValidation';

export type DossierInfoBodyValidationProps = {
    layout: Layout;
};

const DossierInfoBodyValidation: React.FunctionComponent<DossierInfoBodyValidationProps> =
    function (props) {
        const { layout } = props;
        const isDefaultLayout = [LAYOUT_DEFAULT, LAYOUT_DOCUMENTS_1_3].includes(layout);
        const isFullFormLayout = layout === LAYOUT_FORMS_FULL;
        const { dossier } = useSelectedDossierContext();
        const navigate = useNavigate();
        const { setSidebarOpen, setProfileHeaderVisible } = useFullLayout();
        const { persistAllFormulaires, hasNonPersistedChanges } = useDossierFormulaireContext();
        const { modalActions } = useModal();
        const { palette } = useTheme();
        const { setDossierValidationPath, setOnExit, pathOnExit, setActionsBloc } =
            useDossierValidationHeader();
        const { getDossierRoute } = useDossierRoutingContext();

        const handleChangeLayout = (layoutParam: Layout) => {
            // The url can already have different properties in the query, we just have to update only one property
            const urlParams = new URLSearchParams(document.location.search);
            urlParams.set('layout', layoutParam);
            window.open(
                `${document.location.origin + document.location.pathname}?${urlParams.toString()}`,
                '_blank',
            );
        };

        React.useEffect(() => {
            setProfileHeaderVisible(false);

            setActionsBloc(
                <DossierValidationCustomHeaderActions
                    currentLayout={layout}
                    onLayoutChange={handleChangeLayout}
                    dossier={dossier}
                    saveBeforeOpenFullForm={async () => {
                        if (hasNonPersistedChanges) {
                            modalActions.call(
                                <ConfirmationModal
                                    messages={{
                                        question: 'Enregistrer les modifications ?',
                                        detail: {
                                            message:
                                                'Vous avez des modifications en cours sur le formulaire. Souhaitez-vous sauvegarder vos modifications?',
                                            severity: 'warning' as const,
                                        },
                                        validation: 'Sauvegarder',
                                        annulation: 'Continuer',
                                    }}
                                    color={palette.primary}
                                    actionOnValidation={async () => {
                                        await persistAllFormulaires();
                                        handleChangeLayout(LAYOUT_FORMS_FULL);
                                        modalActions.close();
                                    }}
                                    actionOnCancellation={() => {
                                        handleChangeLayout(LAYOUT_FORMS_FULL);
                                        modalActions.close();
                                    }}
                                />,
                            );
                        } else {
                            handleChangeLayout(LAYOUT_FORMS_FULL);
                        }
                    }}
                />,
            );
            return () => {
                setProfileHeaderVisible(true);
                setActionsBloc(null);
            };
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [
            setProfileHeaderVisible,
            setActionsBloc,
            hasNonPersistedChanges,
            layout,
            dossier,
            persistAllFormulaires,
        ]);

        const onExit = useCallback(async () => {
            if (isDefaultLayout && hasNonPersistedChanges) {
                try {
                    await persistAllFormulaires();
                } catch (err) {
                    console.error(err);
                    enqueueSnackbar(
                        'Une erreur est survenue lors de la sauvegarde de votre dossier',
                        { variant: 'error' },
                    );

                    return;
                }
            }
            if (pathOnExit) {
                navigate(pathOnExit);
            } else {
                navigate(getDossierRoute(dossier.id as number));
            }
            enqueueSnackbar('Votre dossier a bien été sauvegardé');
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [
            dossier.id,
            getDossierRoute,
            hasNonPersistedChanges,
            isDefaultLayout,
            navigate,
            persistAllFormulaires,
        ]);

        const commonSx = {
            margin: '1em 0',
        };

        const defaultLayoutSx = {
            ...commonSx,
            display: 'grid',
            gridTemplateColumns: '2fr 1fr',
            gap: '4px',
        };

        const documentLayoutSx = {
            ...commonSx,
        };

        useEffect(() => {
            setSidebarOpen(false);
            setDossierValidationPath(true);
            setOnExit(() => onExit);

            return () => {
                setSidebarOpen(true);
                setDossierValidationPath(false);
                setOnExit(() => {});
            };
        }, [setSidebarOpen, setDossierValidationPath, setOnExit, onExit]);

        if (!dossier.id) {
            return null;
        }

        return (
            <>
                <DossierValidationBreadcrumb
                    isDefaultLayout={isDefaultLayout}
                    isFullFormLayout={isFullFormLayout}
                />
                <Box sx={isDefaultLayout ? defaultLayoutSx : documentLayoutSx}>
                    {!isFullFormLayout && <DossierDocumentsValidation currentLayout={layout} />}
                    {(isDefaultLayout || isFullFormLayout) && (
                        <DossierFormsValidation currentLayout={layout} />
                    )}
                </Box>
            </>
        );
    };

export default DossierInfoBodyValidation;
